import {
  FOLLOW_UP_ITEM_LABELS,
  type ClinicalPhase,
  type FollowUpItem,
} from "@care-by-ella/contracts";
import { ClinicalPhaseField } from "@/components/ClinicalPhaseField";
import {
  SubscriptionPlanFields,
  type PlanChoiceValue,
} from "@/components/SubscriptionPlanChoice";

// TES-1185 (TES-1180 points 1 and 4) — the decision's own parts inside the
// care meeting's final step, when the meeting proceeds with nothing to
// complete: the clinical phase, the follow-up still open, the subscription
// link. The order is the meeting's Recept card; the prescription status is
// the Webdoc step's. The same fields the decision workspace asks for, so the
// two ways to a positive decision ask the same questions.
//
// TES-1400 (Ö-526) — shown whenever the doctor chooses "Godkänd för
// behandling": the decision is that outcome, never a later step, so the
// "Publicera behandlingsbeslutet nu" box is gone.
export function MeetingDecisionFields({
  patientId,
  clinicalPhase,
  onClinicalPhaseChange,
  followUp,
  followUpConfirmed,
  onFollowUpConfirmedChange,
  subscription,
  onSubscriptionChange,
  emailVerified,
  disabled,
}: {
  patientId: string;
  clinicalPhase: ClinicalPhase | null;
  onClinicalPhaseChange: (v: ClinicalPhase | null) => void;
  followUp: readonly FollowUpItem[];
  followUpConfirmed: boolean;
  onFollowUpConfirmedChange: (v: boolean) => void;
  subscription: PlanChoiceValue;
  onSubscriptionChange: (v: PlanChoiceValue) => void;
  emailVerified: boolean;
  disabled?: boolean;
}) {
  return (
    <fieldset
      className="flex flex-col gap-sm rounded-md border border-surfaceMuted p-sm"
      data-testid="meeting-decision"
    >
      <legend className="px-xs font-display text-[15px] text-textPrimary">
        Behandlingsbeslut
      </legend>
      <ClinicalPhaseField
        idPrefix="meeting-decision"
        value={clinicalPhase}
        onChange={onClinicalPhaseChange}
        disabled={disabled}
      />
      {followUp.length > 0 ? (
        <div
          role="status"
          className="rounded-md border border-warning/60 bg-warning/5 p-sm"
        >
          <p className="text-sm font-semibold text-warning">
            Medicinsk uppföljning kvar
          </p>
          <ul className="mt-xs list-disc pl-md text-sm">
            {followUp.map((item) => (
              <li key={item}>{FOLLOW_UP_ITEM_LABELS[item].sv}</li>
            ))}
          </ul>
          <label className="mt-sm flex items-start gap-sm text-sm">
            <input
              type="checkbox"
              className="mt-[3px]"
              checked={followUpConfirmed}
              onChange={(e) => onFollowUpConfirmedChange(e.target.checked)}
              disabled={disabled}
            />
            <span>
              Jag bekräftar att behandlingen aktiveras med medicinsk uppföljning
              kvar
            </span>
          </label>
        </div>
      ) : null}
      <div>
        <p className="mb-xs text-sm font-semibold">Abonnemangslänk</p>
        <SubscriptionPlanFields
          patientId={patientId}
          value={subscription}
          onChange={onSubscriptionChange}
          disabled={disabled}
          name={`meeting-decision-plan-${patientId}`}
        />
        <p className="mt-xs text-xs text-textSecondary">
          {emailVerified
            ? "Länken skickas via e-post när beslutet publiceras. Patienten ser receptet och behandlingsstarten först när abonnemanget är aktiverat."
            : "Patienten saknar verifierad e-postadress. Beslutet kan inte publiceras förrän hon har verifierat sin e-post."}
        </p>
      </div>
    </fieldset>
  );
}
